import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { DevToolSection, devFieldStack, devLabel, devResultPanel, devHint, devFileInput, devMono } from '/imports/ui/DevToolSection';
import { Button } from '/imports/ui/Button';
import { arrayBufferToBase64 } from '/imports/utils';
import { extractChecksum, applyChecksum, ExtractChecksumResult } from '/imports/api/devtools';

export const DevToolChecksum = () => {
  const [dllBase64, setDllBase64] = useState('');
  const [fileName, setFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState<ExtractChecksumResult | null>(null);

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setResult(null);
    setDllBase64('');
    setFileName('');
    if (!f) return;
    setFileName(f.name);
    setDllBase64(arrayBufferToBase64(await f.arrayBuffer()));
  };

  const extract = async () => {
    if (!dllBase64) return toast.error('Choose a FlsClient.dll first');
    setExtracting(true);
    setResult(null);
    try {
      const res = await extractChecksum.call({ dllBase64 });
      setResult(res);
      if (!res.ok) toast.error(res.error || 'Extraction failed');
      else if (!res.changed) toast.info('Checksum matches the current one — no update needed');
      else toast.success(`Found new checksum (${res.version || 'unknown version'})`);
    }
    catch (e: any) {
      toast.error(e?.message || `${e}`);
    }
    finally {
      setExtracting(false);
    }
  };

  const apply = async () => {
    if (!result?.md5) return;
    setApplying(true);
    try {
      const res = await applyChecksum.call({ md5: result.md5 });
      if (res.ok) {
        toast.success('Checksum applied — restart a bot to verify login');
        setResult({ ...result, current: result.md5, changed: false });
      }
      else {
        toast.error(res.error || 'Apply failed');
      }
    }
    catch (e: any) {
      toast.error(e?.message || `${e}`);
    }
    finally {
      setApplying(false);
    }
  };

  return (
    <DevToolSection
      title="Refresh MTGO Login Checksum"
      description="Upload the current <code>FlsClient.dll</code> from the installed MTGO client (<code>…AppData/Local/Apps/2.0/…</code>). Extracts the embedded login md5 and, if it differs from what bots currently send, lets you write it to <code>Settings.md5</code> (the value every bot login sends). A client auto-update changes this value and rejects every bot at login (ClientMd5SumIsInvalid)."
      buttonText={extracting ? 'Extracting...' : 'Extract Checksum'}
      onRun={extract}
      loading={extracting}
    >
      <div className={devFieldStack}>
        <div>
          <label className={devLabel}>
            FlsClient.dll
          </label>
          <input className={devFileInput} type="file" accept=".dll,application/octet-stream" onChange={onFile} />
          {fileName && <div className={devHint}>{fileName}</div>}
        </div>
      </div>

      {result?.ok && (
        <div className={`${devResultPanel} flex flex-col gap-1.5`}>
          <div className={devMono}>version: <strong>{result.version || '—'}</strong></div>
          <div className={devMono}>extracted: <strong>{result.md5}</strong></div>
          <div className={devMono}>current:&nbsp;&nbsp;&nbsp;{result.current || '(none in DB)'}</div>
          <div className={result.changed ? 'text-warn' : 'text-primary'}>
            {result.changed ? 'Differs from current — apply to update bots.' : 'Matches current — nothing to do.'}
          </div>
          {result.changed && (
            <div className="mt-2">
              <Button onClick={apply} disabled={applying}>{applying ? 'Applying...' : 'Apply to Settings'}</Button>
            </div>
          )}
        </div>
      )}
    </DevToolSection>
  );
};
